import { ReactNode } from 'react';
import ReactSelect, {
  components,
  OptionProps,
  Props as ReactSelectProps,
} from 'react-select';
import GenericFormField from '../genericFormField/GenericFormField';
import {
  FieldRendererProps,
  GenericFormFieldBaseProps,
} from '../genericFormField/GenericFormField.types';
import { CustomSelectFieldContainerStyled } from './CustomSelectField.styles';

interface OptionBase {
  label: string | ReactNode;
  value: string;
}

type ReactCustomSelectProps = ReactSelectProps<OptionBase>;

export interface CustomSelectFieldProps
  extends Omit<GenericFormFieldBaseProps, 'fieldRenderer' | 'placeholder'> {
  formatOptionLabel?: ReactCustomSelectProps['formatOptionLabel'];
  isClearable?: boolean;
  isLoading?: boolean;
  isSearchable?: boolean;
  name: string;
  optionRenderer?: (props: OptionProps<OptionBase>) => ReactNode;
  options: ReactCustomSelectProps['options'];
  placeholder?: ReactCustomSelectProps['placeholder'];
  onChangeHandler?: (value: OptionBase['value'] | null) => void;
}

const { GroupHeading, Option } = components;

function getCustomComponents(
  optionRenderer: CustomSelectFieldProps['optionRenderer'],
): ReactCustomSelectProps['components'] {
  const defaultOverrides: ReactCustomSelectProps['components'] = {
    GroupHeading: (props) => (
      <GroupHeading data-testid="select-group-heading" {...props} />
    ),
  };

  if (!optionRenderer) {
    return defaultOverrides;
  }

  return {
    ...defaultOverrides,
    Option: (props) => <Option {...props}>{optionRenderer(props)}</Option>,
  };
}

/**
 * Tries to find the option label given the `value` and all the `options`.
 * It also works for option groups (i.e `{ label: string, options: OptionBase[] }`).
 */
function findOptionLabel(
  options: CustomSelectFieldProps['options'],
  value: string,
) {
  return options?.reduce<ReactNode>((label, currentOption) => {
    if (label !== undefined) {
      return label;
    }

    if ('value' in currentOption && value === currentOption.value) {
      return currentOption.label;
    }

    if ('options' in currentOption) {
      const foundOption = currentOption.options.find(
        (option) => option.value === value,
      );

      return foundOption?.label ?? label;
    }

    return label;
  }, undefined);
}

const CustomSelectField = ({
  disabled,
  formatOptionLabel,
  isClearable,
  isLoading,
  isSearchable,
  name,
  onChangeHandler,
  optionRenderer,
  options,
  placeholder,
  ...rest
}: CustomSelectFieldProps) => {
  const fieldRenderer = ({
    field: { onChange, value: currentValue, onBlur },
    formState: { disabled: isFormDisabled },
  }: FieldRendererProps) => (
    <CustomSelectFieldContainerStyled>
      <ReactSelect<OptionBase>
        options={options}
        onChange={(newValue: OptionBase | null) => {
          const value = newValue?.value ?? null;
          onChange(value); // this 'hack' is required to prevent setting the entire option as value
          onChangeHandler?.(value);
        }}
        // value needs to be provided as an option object in order to have selected value set properly
        value={
          currentValue && {
            label: findOptionLabel(options, currentValue),
            value: currentValue,
          }
        }
        className="custom-select"
        classNamePrefix="select"
        components={getCustomComponents(optionRenderer)}
        isDisabled={disabled || isFormDisabled}
        isSearchable={isSearchable}
        formatOptionLabel={formatOptionLabel}
        placeholder={placeholder}
        isClearable={isClearable}
        isLoading={isLoading}
        onBlur={onBlur}
      />
    </CustomSelectFieldContainerStyled>
  );

  return (
    <GenericFormField name={name} fieldRenderer={fieldRenderer} {...rest} />
  );
};

CustomSelectField.defaultProps = {
  formatOptionLabel: undefined,
  isClearable: true,
  isLoading: false,
  isSearchable: true,
  onChangeHandler: undefined,
  optionRenderer: undefined,
  placeholder: undefined,
};

export default CustomSelectField;
